/**
 * Animations module (IntersectionObserver)
 *
 * Uso básico en markup:
 *  - class="anim" data-anim="bottom|top|left|right|fade"
 *  - class="anim" data-anim="clip-bottom|clip-top|clip-left|clip-right"
 *
 * Parámetros disponibles (data-attributes):
 *  - data-delay="300"         -> delay en ms antes de activar .anim-on
 *  - data-delay-mobile="150"  -> override de delay solo en mobile (< 992px)
 *  - data-speed="0.8"         -> duración en segundos
 *  - data-once="true"         -> anima una sola vez
 *  - data-mobile="false"      -> en mobile no anima (se muestra de inmediato)
 *  - data-reset="top|bottom|both"
 *                              -> controla cuándo resetear al salir del viewport
 *
 * Notas:
 *  - .anim-img espera la carga de <img> antes de activar la animación.
 *  - .force dispara animación al cargar la página sin esperar observer.
 */
function animations() {
  const elements = document.querySelectorAll('.anim')
  if (elements.length === 0) return

  // ── Cache & helpers ──

  let isMobile = window.innerWidth < 992
  let resizeTimer
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => { isMobile = window.innerWidth < 992 }, 150)
  }, { passive: true })

  const shouldAnimate = (el) =>
    (el.dataset.mobile ?? 'true') === 'true' || !isMobile

  // ── Apply animation (batched in rAF) ──

  const activate = (el, once, observer) => {
    const speed = el.dataset.speed ? el.dataset.speed + 's' : '1s'
    requestAnimationFrame(() => {
      el.style.animationDuration = speed
      el.classList.add('anim-on')
    })
    if (once && observer) observer.unobserve(el)
  }

  const applyAnimation = (el, once = false, observer = null) => {
    const delay = parseInt(
      isMobile && el.dataset.delayMobile != null
        ? el.dataset.delayMobile
        : el.dataset.delay,
      10
    ) || 0

    if (delay > 0) {
      setTimeout(() => activate(el, once, observer), delay)
    } else {
      activate(el, once, observer)
    }
  }

  // ── Image-aware animation ──

  const handleImageAnimation = (el, once, observer) => {
    const img = el.querySelector('img')
    if (!img || img.complete) {
      applyAnimation(el, once, observer)
    } else {
      img.addEventListener('load', () => applyAnimation(el, once, observer), { once: true })
    }
  }

  // ── Reset logic ──

  const shouldReset = (el, rect) => {
    const reset = el.dataset.reset
    if (!reset) return true
    if (reset === 'both') return true
    if (reset === 'top') return rect.bottom < 0
    if (reset === 'bottom') return rect.top > window.innerHeight
    return false
  }

  // ── Observer callback ──

  const onIntersect = (entries, observer) => {
    for (const entry of entries) {
      const el = entry.target
      const once = el.dataset.once === 'true'
      const isImg = el.classList.contains('anim-img')

      if (entry.isIntersecting) {
        if (!shouldAnimate(el)) continue
        isImg ? handleImageAnimation(el, once, observer) : applyAnimation(el, once, observer)
      } else if (!once && shouldReset(el, entry.boundingClientRect)) {
        el.classList.remove('anim-on')
      }
    }
  }

  // ── Create observers ──

  const observer = new IntersectionObserver(onIntersect, { threshold: 0.4 })
  const clipObserver = new IntersectionObserver(onIntersect, { threshold: 0 })

  // ── Register elements ──

  elements.forEach(el => {
    // En mobile, si data-mobile="false", mostrar al instante sin animación
    if (isMobile && !shouldAnimate(el)) {
      el.style.animationDuration = '0s'
      el.classList.add('anim-on')
      return
    }

    const isClip = el.dataset.anim && el.dataset.anim.startsWith('clip')
      ; (isClip ? clipObserver : observer).observe(el)
  })

  // ── Force-trigger on load ──

  document.querySelectorAll('.force').forEach(el => {
    if (!shouldAnimate(el)) return
    el.classList.contains('anim-img')
      ? handleImageAnimation(el, false, null)
      : applyAnimation(el, false, null)
  })
}

export default animations
